import { useRef, useEffect, useState } from 'react';
import moment from 'moment';
import { Formik, Form } from 'formik';
import { toast } from 'react-toastify';
import { sortBy } from 'lodash';
import PulseLoader from 'react-spinners/PulseLoader';

import {
  Modal,
  InputImage,
  FormInput,
  FormSelect,
  Label,
  Button,
  Input3DModel,
  TertiaryButton,
} from '@component';
import { useServices } from '@services';
import {
  collection,
  carcasePath,
  skeletonPath,
  animalCollection,
  levelOneCollection,
  levelTwoCollection,
  levelThreeCollection,
  modelPath,
  showActiveToggle,
} from './constants';

const fileNameFromUrl = url => {
  if (!url) return '';
  try {
    const decoded = decodeURIComponent(url);
    const withoutQuery = decoded.split('?')[0];
    const lastSegment = withoutQuery.substring(withoutQuery.lastIndexOf('/') + 1);
    return lastSegment.split('%2F').pop();
  } catch (e) {
    return '';
  }
};

export const Item = ({ item, close, saved, editable = true }) => {
  const [loading, setLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState('general');
  const [animalOptions, setAnimalOptions] = useState([]);
  const [levelOneOptions, setLevelOneOptions] = useState([]);
  const [levelTwoOptions, setLevelTwoOptions] = useState([]);

  const { db } = useServices();

  const validate = values => {
    const errors = {};
    if (!values.animalId) {
      errors.animalId = 'Required';
    }
    if (!values.levelOneId) {
      errors.levelOneId = 'Required';
    }
    if (!values.levelTwoId) {
      errors.levelTwoId = 'Required';
    }
    if (!values.meatcutName) {
      errors.meatcutName = 'Required';
    }
    if (!values.meatcutCode) {
      errors.meatcutCode = 'Required';
    }
    if (!values.meatcutDetail1) {
      errors.meatcutDetail1 = 'Required';
    }
    if (!values.meatcutDetail2) {
      errors.meatcutDetail2 = 'Required';
    }

    return errors;
  };

  const formikRef = useRef();

  useEffect(() => {
    const mounted = { current: true };
    setLoading(true);
    Promise.all([
      db.list(animalCollection),
      db.list(levelOneCollection),
      db.list(levelTwoCollection),
      db.list(levelThreeCollection),
    ])
      .then(([animalOptions, levelOneOptions, levelTwoOptions]) => {
        if (!mounted.current) return;
        setAnimalOptions(animalOptions);
        setLevelOneOptions(levelOneOptions.map(o => ({ ...o, name: o.name.split('$').join(': ') })));
        setLevelTwoOptions(levelTwoOptions.map(o => ({ ...o, name: o.name.split('$').join(': ') })));
      })
      .catch(err => {
        if (mounted.current) toast.error('An unknown error occured');
      })
      .finally(() => {
        if (mounted.current) setLoading(false);
      });
    return () => {
      mounted.current = false;
    };
  }, [db]);

  const update3DModel = (setFieldValue, file) => {
    setFieldValue('modelFile', file);
    // for compatibility
    setFieldValue('modelUrl', file.url);
    setFieldValue('fileSize', file.size);
    setFieldValue('modelFileName', file.name);
  };

  return (
    <Modal
      visible={!!item}
      onClose={close}
      title={item && item.id ? 'View / Edit Meat Products' : 'Add new Meat Products'}
      className="text-black"
    >
      {loading ? (
        <PulseLoader></PulseLoader>
      ) : (
        <Formik
          innerRef={formikRef}
          initialValues={{
            ...item,
            carcaseFileName: item?.carcaseFileName || fileNameFromUrl(item?.carcaseUrl),
            modelFileName: item?.modelFileName || item?.modelFile?.name || '',
            levelThreeId: item?.levelThreeId || '',
            otherImages: item?.otherImages || [],
            textureUrl: item?.textureUrl || '',
            muscleRef: item?.muscleRef || '-1',
            meatcutDetail1: item?.meatcutDetail.split('$')[0],
            meatcutDetail2: item?.meatcutDetail.split('$')[1],
            isActive: item?.isActive ?? true,
          }}
          validate={validate}
          onSubmit={(values, { setSubmitting }) => {
            if (!editable) {
              setSubmitting(false);
              return;
            }
            const combinedDetail = `${values.meatcutDetail1}$${values.meatcutDetail2}`;
            const trimmedCode = values.meatcutCode.trim();

            const checkExisting = values.id
              ? Promise.resolve({ empty: true })
              : db
                  .collection(collection)
                  .where('meatcutCode', '==', trimmedCode)
                  .limit(1)
                  .get();

            checkExisting
              .then(snapshot => {
                if (!snapshot.empty) {
                  toast.error('Meat product with this code already exists');
                  setSubmitting(false);
                  return;
                }

                return db
                  .save(collection, {
                    ...values,
                    meatcutDetail: combinedDetail,
                    meatcutCode: trimmedCode,
                    modifiedOn: moment().unix(),
                  })
                  .then(() => {
                    setSubmitting(false);
                    toast.success('Meat Products saved successfully');
                    saved();
                    close();
                  });
              })
              .catch(err => {
                console.log('🚀 ~ file: Item.jsx ~ line 122 ~ Item ~ err', err);
                toast.error('An unknown error occured');
              });
          }}
        >
          {({ values, isSubmitting }) => (
            <>
              <div className="pb-4 flex">
                <TertiaryButton
                  onClick={() => setSelectedTab('general')}
                  className={`${selectedTab === 'general' && 'bg-primary-500 text-white'} p-2`}
                >
                  General
                </TertiaryButton>
                <TertiaryButton
                  onClick={() => setSelectedTab('images')}
                  className={`${selectedTab === 'images' && 'bg-primary-500 text-white'} p-2 ml-4`}
                >
                  2D Images
                </TertiaryButton>
              </div>
              <Form>
                {selectedTab === 'general' && (
                  <div className="space-y-4">
                    <div className="md:flex space-x-4">
                      <div className="md:w-6/12 space-y-4">
                        <div className="flex space-x-2">
                          <div className="w-3/12">
                            <Label htmlFor="meatcutCode"> Code * </Label>
                            <FormInput name="meatcutCode" disabled={!editable}></FormInput>
                          </div>
                          <div className="w-9/12">
                          <Label htmlFor="meatcutName">Product Description *</Label>
                            <FormInput name="meatcutName" disabled={!editable}></FormInput>
                          </div>
                        </div>

                        <div>
                          <Label htmlFor="levelOneId">Level 1 *</Label>
                          <FormSelect
                            name="levelOneId"
                            options={sortBy(levelOneOptions, ['name'])}
                            placeholder="Select Level 1"
                            disabled={!editable}
                          ></FormSelect>
                        </div>

                        <div>
                          <Label htmlFor="levelTwoId">Level 2 *</Label>
                          <FormSelect
                            name="levelTwoId"
                            options={sortBy(levelTwoOptions, ['name'])}
                            placeholder="Select Level 2"
                            disabled={!editable}
                          ></FormSelect>
                        </div>
                      </div>

                      <div className="md:w-6/12 space-y-4">
                        <div>
                          <Label htmlFor="animalId">Species *</Label>
                          <FormSelect
                            name="animalId"
                            options={sortBy(animalOptions, ['name'])}
                            placeholder="Select Species"
                            disabled={!editable}
                          ></FormSelect>
                        </div>
                        <label>3D Model File</label>
                        <Input3DModel
                          file={values.modelFile}
                          storagePath={modelPath}
                          onChange={modelFile => update3DModel(formikRef.current.setFieldValue, modelFile)}
                          readOnly={!editable}
                        ></Input3DModel>
                      </div>
                    </div>
                    <div className="flex">
                      <div className="w-6/12 pr-2">
                        <Label htmlFor="meatcutDetail1"> Product Preparation * </Label>
                        <FormInput as="textarea" name="meatcutDetail1" rows="8" disabled={!editable}></FormInput>
                      </div>

                      <div className="w-6/12 pl-2">
                        <Label htmlFor="meatcutDetail2"> Points Requiring Specification / Additional Information * </Label>
                        <FormInput as="textarea" name="meatcutDetail2" rows="8" disabled={!editable}></FormInput>
                      </div>
                    </div>
                  </div>
                )}

                {selectedTab === 'images' && (
                  <div className="md:flex space-x-2">
                    <div className="md:w-6/12">
                      <label className="mt-4 md:mt-0">Carcase 2D Image</label>
                      <InputImage
                        imageUrl={values.carcaseUrl}
                        storagePath={carcasePath}
                        onChange={url => {
                          formikRef.current.setFieldValue('carcaseUrl', url);
                          formikRef.current.setFieldValue('carcaseFileName', fileNameFromUrl(url));
                        }}
                        height="350px"
                        readOnly={!editable}
                      ></InputImage>
                    </div>
                    <div className="md:w-6/12">
                      <label className="mt-4">Skeleton 2D Image</label>
                      <InputImage
                        imageUrl={values.skeltonUrl}
                        storagePath={skeletonPath}
                        onChange={url => formikRef.current.setFieldValue('skeltonUrl', url)}
                        height="350px"
                        readOnly={!editable}
                      ></InputImage>
                    </div>
                  </div>
                )}

                {/* <Label htmlFor="muscleRef" className="mt-4">
                  Muscle Ref Code *
                </Label>
                <FormInput name="muscleRef"></FormInput> */}

                {showActiveToggle && (
                  <div className="flex items-center mt-4">
                    <label htmlFor="publicationStatus" className="mr-2">
                      Publication Status
                    </label>
                    <select
                      name="isActive"
                      id="publicationStatus"
                      value={values.isActive ? 'true' : 'false'}
                      onChange={e =>
                        formikRef.current.setFieldValue(
                          'isActive',
                          e.target.value === 'true'
                        )
                      }
                      className="border border-gray-100 rounded-md p-2"
                      disabled={!editable}
                    >
                      <option value="true">Live</option>
                      <option value="false">Draft</option>
                    </select>
                  </div>
                )}

                {editable && (
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={!!isSubmitting}
                      className="btn btn-brand btn-elevate btn-icon-sm mt-12"
                    >
                      {isSubmitting ? <PulseLoader color="white" size={10}></PulseLoader> : 'Submit'}
                    </Button>
                  </div>
                )}
              </Form>
            </>
          )}
        </Formik>
      )}
    </Modal>
  );
};
